import { ChangeDetectorRef, Component, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MarketplaceAuthService } from '../services/marketplace-auth.service';
import { AuthModalService } from '../services/auth-modal.service';

/**
 * Modal de autenticación del marketplace. En desktop entra como drawer desde la
 * derecha; en mobile sube como bottom-sheet desde abajo. Se monta una sola vez en
 * el shell del marketplace y se controla vía AuthModalService. Ver SPEC-016.
 */
@Component({
  selector: 'app-auth-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  host: { '[class.open]': 'modal.isOpen()' },
  styles: [`
    :host{ position:fixed; inset:0; z-index:1000; pointer-events:none; }

    .backdrop{
      position:absolute; inset:0; background:rgba(9,30,50,.45);
      opacity:0; transition:opacity .25s ease; backdrop-filter:blur(1px);
    }
    :host(.open) .backdrop{ opacity:1; pointer-events:auto; }

    /* ---- Panel: desktop = drawer derecho ---- */
    .panel{
      position:absolute; top:0; right:0; height:100%; width:420px; max-width:100vw;
      background:#fff; box-shadow:-14px 0 40px rgba(9,50,80,.22);
      transform:translateX(100%); transition:transform .3s cubic-bezier(.22,.61,.36,1);
      display:flex; flex-direction:column; pointer-events:auto;
    }
    :host(.open) .panel{ transform:translateX(0); }

    .head{
      background:linear-gradient(135deg,#00A7DB,#0f6ea8); color:#fff;
      padding:22px 22px 20px; position:relative; flex:0 0 auto;
    }
    .head h2{ margin:0; font-size:1.35rem; font-weight:800; }
    .head p{ margin:6px 0 0; opacity:.9; font-size:13.5px; }
    .x{
      position:absolute; top:14px; right:14px; width:34px; height:34px; border:0;
      background:rgba(255,255,255,.18); color:#fff; border-radius:50%; cursor:pointer;
      font-size:18px; line-height:1; display:grid; place-items:center;
    }
    .x:hover{ background:rgba(255,255,255,.3); }

    .body{ padding:22px; overflow-y:auto; flex:1; }
    .tabs{ display:flex; margin-bottom:18px; background:#f0f4f8; border-radius:10px; padding:4px; }
    .tabs button{ flex:1; border:0; background:transparent; padding:10px; border-radius:8px; font-weight:700; cursor:pointer; color:#5b6b7b; font-size:13.5px; }
    .tabs button.active{ background:#fff; color:#0f6ea8; box-shadow:0 2px 6px rgba(9,50,80,.08); }
    label{ display:block; font-size:12.5px; font-weight:700; color:#5b6b7b; margin:12px 0 5px; }
    input{ width:100%; padding:12px; border:1px solid #d6dfe8; border-radius:9px; font-size:14px; box-sizing:border-box; }
    input:focus{ outline:0; border-color:#00A7DB; box-shadow:0 0 0 3px rgba(0,167,219,.15); }
    .btn{ background:#00A7DB; color:#fff; border:0; border-radius:10px; padding:13px; font-weight:700; cursor:pointer; width:100%; margin-top:18px; font-size:15px; }
    .btn:disabled{ opacity:.6; cursor:default; }
    .err{ color:#c0392b; font-size:13px; margin-top:12px; }
    .foot{ text-align:center; margin-top:18px; font-size:13px; color:#7a8798; }
    .foot a{ color:#0f6ea8; font-weight:700; text-decoration:none; cursor:pointer; }

    /* ---- Mobile: bottom-sheet que sube ---- */
    @media (max-width:720px){
      .panel{
        top:auto; bottom:0; right:0; left:0; width:100%; height:auto; max-height:92vh;
        border-radius:20px 20px 0 0; box-shadow:0 -14px 40px rgba(9,50,80,.24);
        transform:translateY(100%);
      }
      :host(.open) .panel{ transform:translateY(0); }
      .grabber{ width:44px; height:5px; border-radius:4px; background:rgba(255,255,255,.6); margin:0 auto 10px; }
    }
    .grabber{ display:none; }
    @media (max-width:720px){ .grabber{ display:block; } }
  `],
  template: `
  <div class="backdrop" (click)="close()"></div>

  <div class="panel" role="dialog" aria-modal="true">
    <div class="head">
      <div class="grabber"></div>
      <button class="x" type="button" (click)="close()" aria-label="Cerrar">×</button>
      <h2>{{ modal.mode() === 'login' ? 'Iniciar sesión' : 'Crear cuenta' }}</h2>
      <p>Reserva tu cita sin perder lo que estabas viendo.</p>
    </div>

    <div class="body">
      <div class="tabs">
        <button [class.active]="modal.mode()==='login'" type="button" (click)="setMode('login')">Ingresar</button>
        <button [class.active]="modal.mode()==='registro'" type="button" (click)="setMode('registro')">Registrarme</button>
      </div>

      @if (modal.mode() === 'registro') {
        <label>Nombre</label>
        <input [(ngModel)]="nombre" placeholder="Tu nombre" (keyup.enter)="submit()" />
        <label>Teléfono</label>
        <input type="tel" [(ngModel)]="telefono" placeholder="Ej. 987654321" (keyup.enter)="submit()" />
      }
      <label>Email</label>
      <input type="email" [(ngModel)]="email" placeholder="tu@email.com" (keyup.enter)="submit()" />
      <label>Contraseña</label>
      <input type="password" [(ngModel)]="password" placeholder="••••••" (keyup.enter)="submit()" />

      <button class="btn" type="button" [disabled]="cargando || !email || !password" (click)="submit()">
        {{ cargando ? 'Procesando…' : (modal.mode() === 'login' ? 'Ingresar' : 'Crear cuenta') }}
      </button>
      @if (error) { <div class="err">{{ error }}</div> }

      <!-- Alta de cliente de la clinica (por documento, SPEC-030). Es un wizard de 4
           pasos con verificacion por WhatsApp: no entra en este drawer, tiene pagina. -->
      <div class="foot">
        ¿Sos paciente de la clínica?
        <a (click)="irARegistroCliente()">Creá tu cuenta con tu documento</a>
      </div>
    </div>
  </div>
  `,
})
export class AuthModalComponent {
  email = '';
  password = '';
  nombre = '';
  telefono = '';
  cargando = false;
  error = '';

  constructor(
    public modal: AuthModalService,
    private auth: MarketplaceAuthService,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {}

  /** Cierra el drawer antes de navegar: si no, queda abierto encima del wizard. */
  irARegistroCliente(): void {
    this.close();
    this.router.navigate(['/marketplace/registro']);
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (this.modal.isOpen()) this.close();
  }

  setMode(m: 'login' | 'registro'): void {
    this.modal.mode.set(m);
    this.error = '';
  }

  submit(): void {
    if (!this.email || !this.password || this.cargando) return;
    this.cargando = true;
    this.error = '';
    const obs =
      this.modal.mode() === 'login'
        ? this.auth.login(this.email, this.password)
        : this.auth.registerCliente({
            email: this.email,
            password: this.password,
            nombre: this.nombre,
            telefono: this.telefono,
          });
    obs.subscribe({
      next: () => this.onSuccess(),
      error: (e) => {
        this.error = e?.error?.message ?? 'No se pudo completar. Verifica tus datos.';
        this.cargando = false;
        this.cdr.detectChanges();
      },
    });
  }

  private onSuccess(): void {
    this.cargando = false;
    this.reset();
    // App de clientes: al autenticarse, el cliente se queda donde estaba para seguir
    // reservando. La gestión de negocio vive en front-alphaC (ver SPEC-021 M4).
    this.modal.resolve(true);
  }

  close(): void {
    this.reset();
    this.modal.close();
  }

  private reset(): void {
    this.email = '';
    this.password = '';
    this.nombre = '';
    this.telefono = '';
    this.error = '';
    this.cargando = false;
  }
}
